import {
  Component,
  computed,
  effect,
  inject,
  signal,
  ViewChild,
} from '@angular/core';
import { AdminTabsComponent } from '../components/admin-tabs.component';
import { OnlinePaymentSetupStepComponent } from '../components/online-payment-setup-step.component';
import {
  selectAllRooms,
  selectAuthUserLoading,
  selectAuthVendor,
  selectRoomsLoading,
} from '../../core/store/reducers';
import { Store } from '@ngrx/store';
import { roomsActions } from 'angular/app/core/store/rooms';
import { Room } from '../../core/models/entities/room';
import { BookableBy } from '../../core/models/common/bookable-by';
import { InputOption } from '../../core/models/common/input-option';
import { SharedModule } from '../../shared/shared.module';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { VendorsService } from '../../core/services/vendors.service';
import { Plan } from '../../core/models/common/plan';
import { DestroyableComponent } from '../../shared/components/destroyable/destroyable.component';
import { distinctUntilChanged, skipWhile, takeUntil } from 'rxjs';
import { ToastService } from '../../core/services/toast.service';
import { ApiError } from '../../core/models/common/api-error';
import {
  PaymentCaptureMethod,
  PayrexxInstanceStatus,
  QrInvoiceReminderDelayType,
} from '../../core/models/entities/vendor';
import { authActions } from 'angular/app/core/store/auth';
import { take } from 'rxjs/operators';

import { SharedManagerModule } from '../../shared-manager/shared-manager.module';
import { FormFieldsetCardComponent } from '../../shared/components/form-fieldset-card/form-fieldset-card.component';
import { SavableBeforeExit } from '../../rooms/interfaces/savable-before-exit';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-online-payment-setup-page',
  template: `
    <app-page-header
      [title]="'settings.online-payment.page-title' | transloco"
      [breadcrumbs]="breadcrumbs"
    ></app-page-header>

    <app-spacer></app-spacer>

    <app-admin-tabs></app-admin-tabs>
    <app-spacer size="6"></app-spacer>

    @if (fetchingAccountVerificationStatus()) {
      <app-loader [centered]="true"></app-loader>
    } @else {
      @if (accountVerified()) {
        <app-fieldset-card>
          <div title>{{ 'settings.online-payment.payrexx-config.title' | transloco }}</div>
          <div description>
            {{ 'settings.online-payment.payrexx-config.description' | transloco }}<br />
            {{ 'settings.online-payment.payrexx-config.your-plan' | transloco }}
            <app-badge variant="secondary"
              >{{ ('settings.online-payment.plan.' + vendor()?.plan) | transloco }}
            </app-badge>
          </div>

          <app-online-payment-setup-step
            [completed]="accountVerified()"
            [loadingData]="fetchingAccountVerificationStatus()"
          >
            <div title>{{ 'settings.online-payment.setup-complete.title' | transloco }}</div>
            <div description>
              <p>
                {{ 'settings.online-payment.setup-complete.description' | transloco }}
              </p>
              <div class="my-6 h-px bg-gray-200"></div>
              <app-toggle
                [title]="'settings.online-payment.toggle-payrexx' | transloco"
                [defaultValue]="!vendor().needs_to_complete_payment_setup"
                [disabled]="
                  !vendor().needs_to_complete_payment_setup ||
                  creatingPayrexxInstance()
                "
              ></app-toggle>
            </div>
          </app-online-payment-setup-step>
        </app-fieldset-card>

        <app-spacer size="6"></app-spacer>

        <app-fieldset-card>
          <div title>{{ 'settings.online-payment.capture-amount.title' | transloco }}</div>
          <div description
            ><p>
              {{ 'settings.online-payment.capture-amount.description-line1' | transloco }}
            </p>
            <br />
            <p>
              {{ 'settings.online-payment.capture-amount.description-line2' | transloco }}
            </p></div
          >

          <div class="flex items-center gap-6">
            <app-radio-input
              name="payment_capture_method"
              [control]="form.get('payment_capture_method')"
                [inline]="true"
              [options]="paymentCaptureMethodOptions"
              [marginBottom]="false"
            ></app-radio-input>
            @if (updatingPaymentCaptureMethod()) {
              <div class="mt-2">
                <app-loader size="sm"></app-loader>
              </div>
            }
          </div>
        </app-fieldset-card>

        <app-spacer size="6"></app-spacer>

        <app-fieldset-card
          [showSaveButton]="true"
          [saving]="updatingQrInvoiceMinAmount()"
          [trackFormChanges]="qrInvoiceFormGroup"
          (onSave)="updateQrSettings()"
        >
          <div title>{{ 'settings.online-payment.qr-invoice.title' | transloco }}</div>
          <div description
            ><p>
              {{ 'settings.online-payment.qr-invoice.description-line1' | transloco }}
            </p>
            <br />
            <p>
              {{ 'settings.online-payment.qr-invoice.description-line2' | transloco }}
            </p></div
          >

          <app-single-checkbox-input
            name="qr_invoice_enabled"
            [control]="form.get('qr_invoice_enabled')"
            [label]="'settings.online-payment.qr-invoice.checkbox-label' | transloco"
          ></app-single-checkbox-input>
          <div class="mt-4">
            @if (form.get('qr_invoice_enabled').value) {
              <div class="grid grid-cols-1 gap-6 md:grid-cols-4">
                <div class="md:col-span-2">
                  <app-text-input
                    [label]="'settings.online-payment.qr-invoice.min-amount-label' | transloco"
                    prepend="CHF"
                    name="min_amount"
                    [helpText]="'settings.online-payment.qr-invoice.min-amount-helptext' | transloco"
                    [control]="form.get('qr_invoice_settings.min_amount')"
                  ></app-text-input>
                </div>

                <div class="md:col-span-2">
                  <app-select-input
                    [label]="'settings.online-payment.qr-invoice.due-date-label' | transloco"
                    [helpText]="'settings.online-payment.qr-invoice.due-date-helptext' | transloco"
                    name="due_after_days"
                    [control]="form.get('qr_invoice_settings.due_after_days')"
                    [options]="dueDateOptions"
                  ></app-select-input>
                </div>

                @if (remindersForm) {
                  <div class="md:col-span-3">
                    <app-text-input
                      [label]="'settings.online-payment.qr-invoice.reminder-label' | transloco"
                      name="reminders_0_days"
                      [helpText]="'settings.online-payment.qr-invoice.reminder-helptext' | transloco"
                      [control]="remindersForm.at(0).get('days')"
                      [appendOptions]="reminderDelayTypeOptions"
                      [appendOptionValue]="remindersForm.at(0).value.delay_type"
                      (appendOptionValueChange)="
                        remindersForm
                          .at(0)
                          .controls.delay_type.patchValue($any($event))
                      "
                    ></app-text-input>
                  </div>
                }
              </div>
            }
          </div>
        </app-fieldset-card>
      } @else {
        <app-fieldset-card>
          <div title>{{ 'settings.online-payment.step1.title' | transloco }}</div>
          <div description>
            {{ 'settings.online-payment.step1.description' | transloco }}
          </div>

          <app-online-payment-setup-step [completed]="vendorDataCompleted()">
            <div title>{{ 'settings.online-payment.step1-admin-data.title' | transloco }}</div>
            <div description>
              <p>
                {{ 'settings.online-payment.step1-admin-data.description' | transloco }}
              </p>
            </div>
          </app-online-payment-setup-step>

          <div class="my-8 h-px bg-gray-100"></div>

          <app-online-payment-setup-step
            [completed]="atLeastOnePublicActiveRoom()"
            [loadingData]="loadingRooms()"
          >
            <div title>{{ 'settings.online-payment.step1-public-room.title' | transloco }}</div>
            <div description>
              <p>
                {{ 'settings.online-payment.step1-public-room.description' | transloco }}
              </p>
            </div>
          </app-online-payment-setup-step>
        </app-fieldset-card>

        <app-spacer size="6"></app-spacer>

        <app-fieldset-card>
          <div title>{{ 'settings.online-payment.step2.title' | transloco }}</div>
          <div description>
            {{ 'settings.online-payment.step2.description' | transloco }}
          </div>

          <app-online-payment-setup-step
            [completed]="
              vendor()?.plan === Plan.AnnualSubscription ||
              vendor()?.plan === Plan.CommissionBased
            "
            [loadingData]="!vendor()?.plan || updatingPlan()"
          >
            <div title>{{ 'settings.online-payment.step2-choose-plan.title' | transloco }}</div>
            <div description>
              {{ 'settings.online-payment.step2-choose-plan.description-intro' | transloco }}<br /><a href="/tarifs" target="_blank"
                >{{ 'settings.online-payment.step2-choose-plan.view-calculator' | transloco }}</a
              >
              <i class="fal fa-external-link ml-2"></i>

              <div class="mt-8">
                <app-radio-input
                  name="plan"
                  [control]="form.get('plan')"
                        [inline]="true"
                  [options]="plansOptions"
                  [marginBottom]="false"
                ></app-radio-input>
              </div>
            </div>
          </app-online-payment-setup-step>
        </app-fieldset-card>

        <app-spacer size="6"></app-spacer>

        <app-fieldset-card>
          <div title>{{ 'settings.online-payment.step3.title' | transloco }}</div>
          <div description>
            <p>
              {{ 'settings.online-payment.step3.description-line1' | transloco }}
            </p>
            <p>
              {{ 'settings.online-payment.step3.description-line2' | transloco }}
            </p>
          </div>

          <app-online-payment-setup-step
            [completed]="!vendor()?.needs_to_complete_payment_setup"
          >
            <div title>{{ 'settings.online-payment.step3-create-account.title' | transloco }}</div>
            <div description>
              @if (vendor()?.needs_to_complete_payment_setup) {
                <p>
                  {{ 'settings.online-payment.step3-create-account.description-pending' | transloco }}<br /><br />
                  <strong
                    ><i class="far fa-exclamation-triangle mr-1.5"></i> {{ 'settings.online-payment.step3-create-account.warning' | transloco }}</strong
                  ></p
                >

                <div class="mt-6">
                  <app-button
                    size="sm"
                    variant="secondary"
                    [loading]="creatingPayrexxInstance()"
                    (click)="setupPayrexxInstance()"
                    >{{ 'settings.online-payment.step3-create-account.button' | transloco }}
                  </app-button>
                </div>
              } @else {
                {{ 'settings.online-payment.step3-create-account.description-created' | transloco }}
              }
            </div>
          </app-online-payment-setup-step>

          @if (
            !vendor()?.needs_to_complete_payment_setup && !accountVerified()
          ) {
            <div class="my-8 h-px bg-gray-100"></div>

            <app-online-payment-setup-step
              [completed]="false"
              [customIconWithColor]="
                accountVerificationStatus() === PayrexxInstanceStatus.Review
                  ? 'fa-hourglass-clock text-orange-500'
                  : undefined
              "
            >
              <div title>{{ 'settings.online-payment.step3-submit-request.title' | transloco }}</div>
              <div description>
                @if (
                  accountVerificationStatus() === PayrexxInstanceStatus.Review
                ) {
                  {{ 'settings.online-payment.step3-submit-request.description-review' | transloco }}
                } @else {
                  {{ 'settings.online-payment.step3-submit-request.description-pending' | transloco }}
                }
              </div>
            </app-online-payment-setup-step>
          }
        </app-fieldset-card>
      }
    }
  `,
  imports: [
    AdminTabsComponent,
    SharedManagerModule,
    OnlinePaymentSetupStepComponent,
    SharedModule,
  ],
})
export class OnlinePaymentSetupPageComponent
  extends DestroyableComponent
  implements SavableBeforeExit
{
  @ViewChild(FormFieldsetCardComponent)
  qrInvoiceFieldsetCard: FormFieldsetCardComponent;

  protected readonly Plan = Plan;
  store = inject(Store);
  vendor = this.store.selectSignal(selectAuthVendor);
  private translocoService = inject(TranslocoService);
  form: FormGroup;
  qrInvoiceFormGroup: FormGroup; // Wrapper pour le dirty tracking
  breadcrumbs: Array<{ label: string; routerLink?: string; active?: boolean }> = [];
  plansOptions: InputOption[] = [];
  paymentCaptureMethodOptions: InputOption[] = [];
  dueDateOptions: InputOption[] = [];
  reminderDelayTypeOptions: InputOption[] = [];
  vendorsService = inject(VendorsService);
  toastService = inject(ToastService);
  authUserLoading$ = this.store.select(selectAuthUserLoading);
  loadingRooms = this.store.selectSignal(selectRoomsLoading);
  updatingPlan = signal<boolean>(false);
  updatingPaymentCaptureMethod = signal<boolean>(false);
  updatingQrInvoiceMinAmount = signal<boolean>(false);
  creatingPayrexxInstance = signal<boolean>(false);
  accountVerificationStatus = signal<PayrexxInstanceStatus | null>(null);
  accountVerified = computed(
    () => this.accountVerificationStatus() === PayrexxInstanceStatus.Approved,
  );
  fetchingAccountVerificationStatus = signal<boolean>(false);

  vendorDataCompleted = computed<boolean>(() => {
    return (
      !!this.vendor()?.address &&
      !!this.vendor()?.zip &&
      !!this.vendor()?.city &&
      !!this.vendor()?.email &&
      !!this.vendor()?.phone &&
      !!this.vendor()?.general_terms_public_url
    );
  });

  atLeastOnePublicActiveRoom = computed<boolean>(() => {
    const rooms = this.store.selectSignal(selectAllRooms);
    return rooms()?.some((room: Room) => {
      return room.bookable_by !== BookableBy.Private && room.active;
    });
  });

  get remindersForm(): FormArray<
    FormGroup<{
      days: FormControl<number>;
      fee: FormControl<number>;
      delay_type: FormControl<QrInvoiceReminderDelayType>;
    }>
  > {
    return this.form?.get('qr_invoice_settings.reminders') as FormArray;
  }

  constructor() {
    super();

    this.store.dispatch(roomsActions.findAll({}));
    const vendorReminders = this.vendor()?.qr_invoice_settings?.reminders;

    this.form = new FormGroup({
      plan: new FormControl(this.vendor()?.plan),
      payment_capture_method: new FormControl(
        this.vendor()?.payment_capture_method,
      ),
      qr_invoice_enabled: new FormControl<boolean>(
        !!this.vendor()?.qr_invoice_enabled,
      ),
      qr_invoice_settings: new FormGroup({
        min_amount: new FormControl(
          this.vendor()?.qr_invoice_settings?.min_amount ?? 0,
          [Validators.min(0)],
        ),
        due_after_days: new FormControl(
          this.vendor()?.qr_invoice_settings?.due_after_days ?? 30,
        ),
        reminders: new FormArray([
          new FormGroup({
            days: new FormControl<number>(
              vendorReminders?.length > 0 ? vendorReminders[0].days : 0,
            ),
            fee: new FormControl<number>(0),
            delay_type: new FormControl<QrInvoiceReminderDelayType>(
              vendorReminders?.length > 0
                ? vendorReminders[0].delay_type
                : QrInvoiceReminderDelayType.DaysAfterDueDate,
            ),
          }),
        ]),
      }),
    });

    // Créer un FormGroup wrapper pour le dirty tracking de la section QR Invoice
    this.qrInvoiceFormGroup = new FormGroup({
      qr_invoice_enabled: this.form.get('qr_invoice_enabled'),
      qr_invoice_settings: this.form.get('qr_invoice_settings'),
    });

    effect(() => {
      if (this.vendor()) {
        // après chargement du vendor (si on arrive sur la page avec un refresh)
        this.form
          .get('payment_capture_method')
          .setValue(this.vendor().payment_capture_method, { emitEvent: false });
      }
    });
  }

  ngOnInit() {
    // Initialize translated breadcrumbs and options
    this.translocoService
      .selectTranslation()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(() => {
        this.plansOptions = [
          { value: Plan.AnnualSubscription, label: this.translocoService.translate('settings.online-payment.plan.ANNUAL') },
          { value: Plan.CommissionBased, label: this.translocoService.translate('settings.online-payment.plan.COMMISSION') },
        ];
        this.paymentCaptureMethodOptions = [
          { value: PaymentCaptureMethod.Immediate, label: this.translocoService.translate('settings.online-payment.capture-method.IMMEDIATE') },
          { value: PaymentCaptureMethod.OnApproval, label: this.translocoService.translate('settings.online-payment.capture-method.ON_APPROVAL') },
        ];

        this.breadcrumbs = [
          { label: this.translocoService.translate('nav.settings'), routerLink: '/administration' },
          {
            label: this.translocoService.translate('settings.online-payment.breadcrumb'),
            routerLink: '/administration/paiement-en-ligne',
          },
        ];

        this.dueDateOptions = [
          { value: 5, label: this.translocoService.translate('settings.online-payment.qr-invoice.due-5days') },
          { value: 10, label: this.translocoService.translate('settings.online-payment.qr-invoice.due-10days') },
          { value: 20, label: this.translocoService.translate('settings.online-payment.qr-invoice.due-20days') },
          { value: 30, label: this.translocoService.translate('settings.online-payment.qr-invoice.due-30days') },
          { value: 40, label: this.translocoService.translate('settings.online-payment.qr-invoice.due-40days') },
          { value: 50, label: this.translocoService.translate('settings.online-payment.qr-invoice.due-50days') },
          { value: 60, label: this.translocoService.translate('settings.online-payment.qr-invoice.due-60days') },
        ];

        this.reminderDelayTypeOptions = [
          {
            label: this.translocoService.translate('settings.online-payment.qr-invoice.reminder-days-before'),
            value: 'days_before_due_date',
          },
          {
            label: this.translocoService.translate('settings.online-payment.qr-invoice.reminder-days-after-due'),
            value: 'days_after_due_date',
          },
          {
            label: this.translocoService.translate('settings.online-payment.qr-invoice.reminder-days-after-invoice'),
            value: 'days_after_invoice_date',
          },
        ];
      });

    if (!this.vendor()?.needs_to_complete_payment_setup) {
      // Une fois le compte créé, on peut vérifier son statut
      this.fetchingAccountVerificationStatus.set(true);
      this.vendorsService
        .findPayrexxVerificationStatus()
        .then((response) => {
          this.accountVerificationStatus.set(response.data.status);
          this.fetchingAccountVerificationStatus.set(false);
        })
        .catch(() => {
          this.fetchingAccountVerificationStatus.set(false);
        });
    }

    this.form
      .get('plan')
      .valueChanges.pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
      .subscribe(async (plan: Plan) => {
        this.updatingPlan.set(true);
        try {
          await this.vendorsService.update({
            name: this.vendor().name,
            slug: this.vendor().slug,
            plan: plan,
          });
          this.store.dispatch(authActions.findCurrentUser());
          this.authUserLoading$
            .pipe(
              skipWhile((loading) => loading === true),
              take(1),
            )
            .subscribe(() => {
              this.updatingPlan.set(false);
            });
        } catch (exception) {
          this.toastService.error((exception as ApiError).message);
          this.form
            .get('plan')
            .setValue(this.vendor()?.plan, { emitEvent: false });
          this.updatingPlan.set(false);
        }
      });

    this.form
      .get('payment_capture_method')
      .valueChanges.pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
      .subscribe(async (paymentCaptureMethod: PaymentCaptureMethod) => {
        this.updatingPaymentCaptureMethod.set(true);
        try {
          await this.vendorsService.update({
            name: this.vendor().name,
            slug: this.vendor().slug,
            payment_capture_method: paymentCaptureMethod,
          });
          this.store.dispatch(authActions.findCurrentUser());
          this.toastService.success(this.translocoService.translate('settings.online-payment.toast-success-modifications'));
        } catch (exception) {
          this.toastService.error((exception as ApiError).message);
          this.form
            .get('payment_capture_method')
            .setValue(this.vendor()?.payment_capture_method, {
              emitEvent: false,
            });
        } finally {
          this.updatingPaymentCaptureMethod.set(false);
        }
      });

    this.form
      .get('qr_invoice_enabled')
      .valueChanges.pipe(takeUntil(this.onDestroy$), distinctUntilChanged())
      .subscribe((qrInvoiceEnabled) => {
        if (qrInvoiceEnabled) {
          this.form
            .get('qr_invoice_settings.min_amount')
            .setValidators([Validators.required]);
          this.form
            .get('qr_invoice_settings.due_after_days')
            .setValidators([Validators.required]);
        } else {
          this.form.get('qr_invoice_settings.min_amount').clearValidators();
          this.form.get('qr_invoice_settings.due_after_days').clearValidators();
        }
        this.form
          .get('qr_invoice_settings.min_amount')
          .updateValueAndValidity();
        this.form
          .get('qr_invoice_settings.due_after_days')
          .updateValueAndValidity();
      });
  }

  async updateQrSettings() {
    this.updatingQrInvoiceMinAmount.set(true);
    try {
      const formValues = this.form.getRawValue();
      await this.vendorsService.update({
        name: this.vendor().name,
        slug: this.vendor().slug,
        qr_invoice_enabled: formValues.qr_invoice_enabled,
        qr_invoice_settings: formValues.qr_invoice_settings,
      });
      this.store.dispatch(authActions.findCurrentUser());
      this.toastService.success(this.translocoService.translate('settings.online-payment.toast-success-modifications'));

      // Réinitialiser le dirty state
      this.qrInvoiceFieldsetCard?.resetDirtyState();
      return true;
    } catch (exception) {
      this.toastService.error((exception as ApiError).message);
      this.form
        .get('qr_invoice_settings')
        .setValue(this.vendor()?.qr_invoice_settings, {
          emitEvent: false,
        });
      return false;
    } finally {
      this.updatingQrInvoiceMinAmount.set(false);
    }
  }

  async setupPayrexxInstance() {
    try {
      this.creatingPayrexxInstance.set(true);
      await this.vendorsService.createPayrexxMerchantInstance();
      this.store.dispatch(authActions.findCurrentUser());
      this.toastService.success(
        this.translocoService.translate('settings.online-payment.toast-success-payrexx-created'),
      );
    } catch (exception) {
      this.toastService.error((exception as ApiError).message);
    } finally {
      this.creatingPayrexxInstance.set(false);
    }
  }

  async saveChanges(): Promise<boolean> {
    if (this.qrInvoiceFormGroup.dirty) {
      return await this.updateQrSettings();
    }
    return true;
  }

  protected readonly PayrexxInstanceStatus = PayrexxInstanceStatus;
}
